import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLoader } from '@/layouts/components/PageLoader/PageLoader';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { warning } from '@/assets/images/emojis';
import { useOnboarding } from '@/features/onboarding';
import { routes } from '@/routes/routes';
import { ApiError } from '@/types';
import { useScoring } from '../../hooks/useScoring';
import { BcAttemptState, ScoringErrorCode } from '../../types/scoring';
import styles from './ScoringStartPage.module.scss';

// Starts (or refreshes) the BC score and sends the user to the step the
// backend asks for. The score itself is generated in the background.
export function ScoringStartPage() {
  const navigate = useNavigate();
  const { initiateScoreObtaining } = useScoring();
  const { getOnboardingState } = useOnboarding();
  const [hasFailed, setHasFailed] = useState(false);
  const hasStarted = useRef(false);

  const goToTerms = useCallback(async () => {
    await getOnboardingState();
    navigate(routes.scoring.terms);
  }, [getOnboardingState, navigate]);

  const start = useCallback(async () => {
    setHasFailed(false);

    try {
      const attempt = await initiateScoreObtaining();

      if (attempt.termsRequired) {
        await goToTerms();
        return;
      }

      // Backend running without phone scoring: a score needs KYC first.
      if (attempt.kycRequired) {
        navigate(routes.kyc.base);
        return;
      }

      if (attempt.bcAttemptState === BcAttemptState.NeedPhoneVerification) {
        navigate(routes.scoring.phones);
        return;
      }

      // Would send the wizard straight back here.
      if (attempt.bcAttemptState === BcAttemptState.Undefined) {
        setHasFailed(true);
        return;
      }

      navigate(routes.wizard.base);
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.message === ScoringErrorCode.TermsNotAccepted
      ) {
        await goToTerms();
        return;
      }

      setHasFailed(true);
    }
  }, [initiateScoreObtaining, goToTerms, navigate]);

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }

    hasStarted.current = true;
    void start();
  }, [start]);

  if (!hasFailed) {
    return <PageLoader />;
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <img src={warning} alt="Warning emoji" className={styles.emoji} />
        <h1 className={styles.title}>Nu am putut verifica istoricul tău</h1>
        <p className={styles.description}>
          Întâmpinăm o problemă tehnică la Biroul de Credit. Te rugăm să
          încerci din nou.
        </p>
      </div>

      <div className={styles.bottomSection}>
        <Button
          trackingId="retry_bc_scoring"
          text="Încearcă din nou"
          rightIcon={<Icon name="rotate-left" />}
          onClick={() => void start()}
          className={styles.retryButton}
        />
      </div>
    </div>
  );
}

export default ScoringStartPage;
